// Import extendTheme from Chakra UI
import { extendTheme } from '@chakra-ui/react'

// Define your color palette
const colors = {
  background: {
    shade1: '#FAFAF8',  // Warm white - main background
    shade2: '#F5F4F2',  // Warm light gray - cards, inputs
    shade3: '#EDEBE8',  // Warm medium gray - hover states
    shade4: '#E4E2DE',  // Warm darker gray - active states
  },
  border: {
    light: '#E8E6E3',   // Subtle borders
    default: '#D4D2CE', // Standard borders
    strong: '#B8B5B0',  // Emphasized borders
  },
  theme: {
    teal: '#3B6B6B',        // Primary brand color (matched to survey)
    tealLight: '#4A7C7C',   // Hover state
    tealDark: '#2D5454',    // Active state
    brown: '#956225',
    darkGray: '#4A443F',
    black: '#1A1614',
    white: '#ffffff',
    lightGray: '#8e9dc3',
    mediumGray: '#6E6761',
    turquoise: '#1f4f94',
    rust: '#734435',
    charcoal: '#272727',
    terracotta: '#8f5b45',
  }
}

// Semantic color mapping for consistent usage across components
const semanticColors = {
  action: {
    primary: 'theme.teal',
    secondary: 'theme.brown',
    subtle: 'theme.mediumGray',
  },
  text: {
    primary: 'theme.black',
    secondary: 'theme.darkGray',
    muted: 'theme.mediumGray',
  },
  border: {
    default: 'border.default',
    subtle: 'border.light',
    strong: 'border.strong',
  },
  feedback: {
    success: 'green.600',
    error: 'red.600',
    warning: 'orange.500',
    info: 'theme.teal',
  },
  state: {
    draft: 'theme.mediumGray',
    staged: 'orange.500',
    live: 'green.600',
    closed: 'theme.rust',
  },
};

// Shadows for depth
const shadows = {
  subtle: '0 1px 3px rgba(26, 22, 20, 0.06)',
  card: '0 1px 4px rgba(26, 22, 20, 0.08)',
  elevated: '0 4px 12px rgba(26, 22, 20, 0.1)',
};

// Define the fonts - Inter everywhere for clean, modern feel
const fonts = {
  heading: "'Inter', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
  body: "'Inter', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
}

// Extend the default theme with your custom colors and fonts
const theme = extendTheme({
  colors: {
    ...colors,
    semantic: semanticColors,
  },
  fonts: fonts,
  shadows: shadows,
  textStyles: {
    h1: {
      fontFamily: 'heading',
      fontWeight: '600',
      fontSize: ['32px', '40px'],
      letterSpacing: '-0.02em',
    },
    h2: {
      fontFamily: 'heading',
      fontWeight: '600',
      fontSize: ['24px', '30px'],
      letterSpacing: '-0.01em',
    },
    h3: {
      fontFamily: 'heading',
      fontWeight: '600',
      fontSize: ['18px', '22px'],
    },
    h4: {
      fontFamily: 'heading',
      fontWeight: '500',
      fontSize: ['16px', '18px'],
    },
    p: {
      fontFamily: 'body',
      fontWeight: 'normal',
      fontSize: ['14px', '15px'],
      lineHeight: 1.6,
    },
    'p-bold': {
      fontFamily: 'body',
      fontWeight: '600',
      fontSize: ['14px', '15px'],
      lineHeight: 1.6,
    },
    'p-italic': {
      fontFamily: 'body',
      fontWeight: 'normal',
      fontStyle: 'italic',
      fontSize: ['14px', '15px'],
    },
    caption: {
      fontFamily: 'body',
      fontWeight: '500',
      fontSize: ['12px', '13px'],
      letterSpacing: '0.01em',
    },
    label: {
      fontFamily: 'body',
      fontWeight: '500',
      fontSize: '14px',
      color: 'theme.darkGray',
    },
  },
  components: {
    Heading: {
      baseStyle: {
        color: 'theme.black',
      },
      variants: {
        h1: { textStyle: 'h1' },
        h2: { textStyle: 'h2' },
        h3: { textStyle: 'h3' },
        h4: { textStyle: 'h4' },
      }
    },
    Text: {
      baseStyle: {
        color: 'theme.darkGray',
      },
      variants: {
        p: { textStyle: 'p' },
        'p-bold': { textStyle: 'p-bold' },
        'p-italic': { textStyle: 'p-italic' },
        caption: { textStyle: 'caption' },
      }
    },
    Input: {
      variants: {
        filled: {
          field: {
            bg: 'white',
            color: 'theme.black',
            border: '1px solid',
            borderColor: 'border.default',
            borderRadius: 'md',
            _hover: {
              bg: 'white',
              borderColor: 'border.strong',
            },
            _focus: {
              bg: 'white',
              borderColor: 'theme.teal',
              boxShadow: '0 0 0 1px var(--chakra-colors-theme-teal)',
            },
          },
        },
      },
      defaultProps: {
        variant: 'filled',
      },
    },
    Textarea: {
      variants: {
        filled: {
          bg: 'white',
          color: 'theme.black',
          border: '1px solid',
          borderColor: 'border.default',
          borderRadius: 'md',
          _hover: {
            bg: 'white',
            borderColor: 'border.strong',
          },
          _focus: {
            bg: 'white',
            borderColor: 'theme.teal',
            boxShadow: '0 0 0 1px var(--chakra-colors-theme-teal)',
          },
        },
      },
      defaultProps: {
        variant: 'filled',
      },
    },
    Select: {
      variants: {
        filled: {
          field: {
            bg: 'white',
            color: 'theme.black',
            border: '1px solid',
            borderColor: 'border.default',
            borderRadius: 'md',
            _hover: {
              bg: 'white',
              borderColor: 'border.strong',
            },
            _focus: {
              bg: 'white',
              borderColor: 'theme.teal',
            },
          },
        },
      },
      defaultProps: {
        variant: 'filled',
      },
    },
    Button: {
      baseStyle: {
        fontWeight: '500',
        borderRadius: 'md',
      },
      variants: {
        primary: {
          bg: 'theme.teal',
          color: 'white',
          _hover: {
            bg: 'theme.tealLight',
          },
          _active: {
            bg: 'theme.tealDark',
          },
        },
        secondary: {
          bg: 'white',
          color: 'theme.black',
          border: '1px solid',
          borderColor: 'border.default',
          _hover: {
            bg: 'background.shade2',
            borderColor: 'border.strong',
          },
        },
        tertiary: {
          bg: 'transparent',
          color: 'theme.mediumGray',
          _hover: {
            color: 'theme.black',
            bg: 'background.shade2',
          },
        },
        danger: {
          bg: 'white',
          color: 'red.600',
          border: '1px solid',
          borderColor: 'border.default',
          _hover: {
            bg: 'red.50',
            borderColor: 'red.200',
          },
        },
        ghost: {
          color: 'theme.darkGray',
          _hover: {
            bg: 'background.shade2',
          },
        },
      },
      defaultProps: {
        variant: 'secondary',
      },
    },
    AccordionButton: {
      variants: {
        question: {
          bg: 'white',
          color: 'theme.black',
          p: 4,
          _hover: {
            bg: 'background.shade2',
          },
          _expanded: {
            bg: 'background.shade2',
          },
        },
        section: {
          bg: 'white',
          color: 'theme.black',
          border: '1px solid',
          borderColor: 'border.default',
          borderRadius: 'md',
          p: 4,
          _hover: {
            bg: 'background.shade2',
          },
          _expanded: {
            bg: 'background.shade2',
            borderBottomRadius: 0,
          },
        },
      },
    },
    AccordionItem: {
      variants: {
        question: {
          border: '1px solid',
          borderColor: 'border.default',
          borderRadius: 'md',
          mb: 2,
          overflow: 'hidden',
        },
      },
    },
    AccordionPanel: {
      variants: {
        section: {
          bg: 'white',
          border: '1px solid',
          borderColor: 'border.default',
          borderTop: 'none',
          borderTopRadius: 0,
          borderBottomRadius: 'md',
          p: 4,
        },
      },
    },
    Card: {
      variants: {
        blackbird: {
          container: {
            bg: 'white',
            border: '1px solid',
            borderColor: 'border.light',
            borderRadius: 'md',
            boxShadow: 'subtle',
          },
        },
      },
      defaultProps: {
        variant: 'blackbird',
      },
    },
    Table: {
      variants: {
        simple: {
          th: {
            color: 'theme.darkGray',
            fontWeight: '600',
            textTransform: 'none',
            letterSpacing: 'normal',
            borderColor: 'border.light',
          },
          td: {
            borderColor: 'border.light',
          },
        },
      },
    },
    Badge: {
      baseStyle: {
        fontWeight: '500',
        borderRadius: 'md',
      },
    },
    Tabs: {
      variants: {
        line: {
          tab: {
            color: 'theme.mediumGray',
            fontWeight: '500',
            _selected: {
              color: 'theme.teal',
              borderColor: 'theme.teal',
            },
            _hover: {
              color: 'theme.black',
            },
          },
        },
      },
    },
    Modal: {
      baseStyle: {
        dialog: {
          bg: 'white',
          borderRadius: 'lg',
        },
        header: {
          fontWeight: '600',
        },
      },
    },
    Alert: {
      variants: {
        subtle: {
          container: {
            borderRadius: 'md',
          },
        },
      },
    },
  },
});

export default theme;
